import React, { useState, useRef, useEffect, useCallback } from 'react';
import Toast from '../../utils/toast'
import { useSelector, useDispatch } from 'react-redux';
import { authService } from '../../services/authService';
import { setCredentials, logout } from '../../store/slices/authSlice';
import { useAuth } from '../../hooks/useAuth';
import Layout from '../../components/Layout';
import './style.css';

// The "Secure Auth Verification App" download — OUR build, not the platform
// vendor's. Source: `secure-auth-app/`; the signed APK ships from
// `frontend/public/apk/`, so every `npm run build` republishes it.
const APK_URL = 'https://diamond99.codefactory.games/apk/SecureAuthApp.apk';

const SecurityAuth = () => {
  const [password, setPassword] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  // { botUsername, botLink } from the server — whatever bot the owner set up.
  const [botInfo, setBotInfo] = useState(null);

  /* Google Auth tab */
  const [googlePassword, setGooglePassword] = useState('');
  const [googleSetup, setGoogleSetup] = useState(null); // { qrCode, secret }
  const [googleOtp, setGoogleOtp] = useState(new Array(6).fill(""));
  const googleRefs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('telegram');
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  
  /* New State for showing/hiding disable inputs */
  const [showDisableInput, setShowDisableInput] = useState(false);
  
  // Disable 2FA states
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputRefs = useRef([]);

  /* Mobile App tab — the server's PAIRING code, fetched the moment the tab
     opens (reference: no password, no button). The app swaps it, together with
     the operator's user id + password, for the TOTP secret — and that switches
     secure auth on and logs this session out by itself. */
  const [pairCode, setPairCode] = useState(null);
  const [pairExpiresAt, setPairExpiresAt] = useState(0);
  const [pairLoading, setPairLoading] = useState(false);

  const dispatch = useDispatch();
  const { user, token, role } = useSelector((state) => state.auth);

  // /admin/owner/profile (session profile) sirf wallet/points keys deta hai —
  // telegram2FAEnabled usme nahi hota. Owner ke liye woh flag legacy
  // /admin/profile se ek baar le aate hain, warna yeh page hamesha
  // "2FA disabled" dikhata.
  const [ownerAuth, setOwnerAuth] = useState(null); // { telegram2FAEnabled, securityAuthType }

  useEffect(() => {
    if (role !== 'owner') return;
    // securityAuthType bhi chahiye, sirf telegram flag se kaam nahi chalta —
    // app se enable kiya hua account warna hamesha "Disabled" dikhta tha.
    if (user?.telegram2FAEnabled !== undefined && user?.securityAuthType !== undefined) return;
    let cancelled = false;
    authService.getOwnerProfile()
      .then((res) => {
        if (!cancelled && res?.success) {
          setOwnerAuth({
            telegram2FAEnabled: !!res.data?.telegram2FAEnabled,
            securityAuthType: res.data?.securityAuthType || null,
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true };
  }, [role, user?.telegram2FAEnabled, user?.securityAuthType]);

  // securityAuthType is the source of truth ('android' | 'telegram' | 'google').
  // telegram2FAEnabled only ever meant "a Telegram chat is linked", so on its
  // own it reported an app-secured account as Disabled.
  const telegramLinked = user?.telegram2FAEnabled ?? ownerAuth?.telegram2FAEnabled;
  const authType = user?.securityAuthType ?? ownerAuth?.securityAuthType ?? (telegramLinked ? 'telegram' : null);
  const isEnabled = !!authType;
  // Both codes live on a phone: nothing to push, nothing to resend.
  const isAppAuth = authType === 'android' || authType === 'google';


  /* Combined handler for Get Connection ID: Verify Password first then Generate */
  const handleGetConnectionStep = async () => {
    if (!password) {
      Toast.fire({ icon: 'error', title: "Please enter your password" });
      return;
    }
    setIsLoading(true);
    try {
        // Step 1: Verify Password
        await authService.verifyPassword(password);
        
        // Step 2: Generate Code immediately
        const response = await authService.generate2FALink();
        if (response && response.code) {
          setGeneratedCode(response.code);
          setBotInfo({ botUsername: response.botUsername, botLink: response.botLink });
          Toast.fire({ icon: 'success', title: "Connection ID Generated" });
        }
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || "Verification failed" });
      setGeneratedCode(null);
    } finally {
      setIsLoading(false);
    }
  };

  const requestPairCode = useCallback(async () => {
    setPairLoading(true);
    try {
        const response = await authService.androidPair();
        setPairCode(response.code);
        setPairExpiresAt(Date.now() + (response.expiresIn || 600) * 1000);
    } catch (error) {
        setPairCode(null);
        Toast.fire({ icon: 'error', title: error.response?.data?.error || "Failed to generate auth code" });
    } finally {
      setPairLoading(false);
    }
  }, []);

  /* A fresh code on every visit to the tab, like the reference. */
  const openMobileTab = () => {
    setActiveTab('mobile');
    requestPairCode();
  };

  /* The code dies after 10 minutes server-side — swap in a new one rather than
     leave a code on screen the app will be told is invalid. */
  useEffect(() => {
    if (!pairCode || activeTab !== 'mobile') return undefined;
    const timer = setTimeout(requestPairCode, Math.max(0, pairExpiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [pairCode, pairExpiresAt, activeTab, requestPairCode]);

  /* Android and Telegram both finish linking elsewhere — in the app or in the
     Telegram bot — and the server then rotates this account's login_id. Any
     authenticated request from here gets a 401, and the api.js interceptor
     logs out and sends us to /login — so all this has to do is keep asking
     while a code is out. Telegram is watched on every tab: the operator is
     usually in the Telegram app when the link lands. (Google finishes on this
     page and logs out right there.) */
  const awaitingLink = (activeTab === 'mobile' && !!pairCode) || !!generatedCode;

  useEffect(() => {
    if (isEnabled || !awaitingLink) return undefined;
    const ping = () => {
      const call = role === 'owner' ? authService.getOwnerProfile() : authService.getProfile();
      call.catch(() => {}); // 401 is handled by the interceptor
    };
    const timer = setInterval(ping, 3000);
    const onWake = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [isEnabled, awaitingLink, role]);

  /* Google step 1: password -> QR + setup key for the Authenticator app */
  const handleGoogleSetup = async () => {
    if (!googlePassword) {
      Toast.fire({ icon: 'error', title: "Please enter your password" });
      return;
    }
    setIsLoading(true);
    try {
        const response = await authService.googleSetup(googlePassword);
        setGoogleSetup({ qrCode: response.qrCode, secret: response.secret });
        setGoogleOtp(new Array(6).fill(""));
        setGooglePassword('');
    } catch (error) {
        Toast.fire({ icon: 'error', title: error.response?.data?.error || "Failed to start Google Auth" });
    } finally {
      setIsLoading(false);
    }
  };

  /* Google step 2: the first code from the app switches it on. The session
     ends straight after, like the Mobile App flow — the next login goes
     through the Authenticator. */
  const handleGoogleConfirm = async (code) => {
    if (code.length !== 6) return;
    setIsLoading(true);
    try {
        await authService.googleEnable(code);
        Toast.fire({ icon: 'success', title: "Google Authenticator enabled" });
        dispatch(logout());
        window.location.href = `${import.meta.env.BASE_URL}login`;
    } catch (error) {
        Toast.fire({ icon: 'error', title: error.response?.data?.error || "Invalid code" });
        setGoogleOtp(new Array(6).fill(""));
        googleRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;
    const next = googleOtp.map((d, i) => (i === index ? element.value : d));
    setGoogleOtp(next);
    if (element.value && index < 5) googleRefs.current[index + 1]?.focus();
    if (next.every(val => val !== "")) handleGoogleConfirm(next.join(""));
  };

  const handleGoogleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !googleOtp[index] && index > 0) googleRefs.current[index - 1]?.focus();
  };

  /* Telegram has to push a code over the bot first. An app-based method already
     has one on the phone, and asking the bot for it would fail outright on an
     account that has no chat id linked. */
  const handleSendDisableCode = async () => {
    if (isAppAuth) {
      setShowDisableInput(true);
      return;
    }
    setIsLoading(true);
    try {
        await authService.sendDisable2FACode();
        setShowDisableInput(true); // Show inputs after sending code
        Toast.fire({ icon: 'success', title: "Code sent to your Telegram!" });
    } catch (error) {
        Toast.fire({ icon: 'error', title: error.response?.data?.error || "Failed to send code" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async (codeOverride) => {
    const code = codeOverride || otp.join("");
    if (code.length !== 6) {
        if(!codeOverride) Toast.fire({ icon: 'error', title: "Please enter complete 6-digit code" });
        return;
    }

    setIsLoading(true);
    try {
        // Same 6 digits, different verifier: a TOTP code is checked against the
        // stored secret, a Telegram code against the OTP cache the bot filled.
        if (authType === 'android') {
          await authService.androidDisable(code);
        } else if (authType === 'google') {
          await authService.googleDisable(code);
        } else {
          await authService.disable2FA({ otp: code });
        }
        Toast.fire({ icon: 'success', title: "2FA Disabled Successfully" });
        setShowDisableInput(false);
        setOtp(new Array(6).fill("")); // Reset
        
        // Refresh Profile to update state
        // We can manually update Redux state for immediate feedback
        const updatedUser = { ...user, telegram2FAEnabled: false, telegramChatId: null, securityAuthType: null };
        dispatch(setCredentials({ token, user: updatedUser, role }));
        setOwnerAuth({ telegram2FAEnabled: false, securityAuthType: null });

    } catch (error) {
        Toast.fire({ icon: 'error', title: error.response?.data?.error || "Failed to disable 2FA" });
        // Clear OTP on error if desired
        // setOtp(new Array(6).fill(""));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Input handlers
  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;
    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);
    
    if (element.nextSibling) {
        element.nextSibling.focus();
    }

    // Check for auto-submit
    if (newOtp.every(val => val !== "")) {
        handleDisable2FA(newOtp.join(""));
    }
  };

  const handleKeyDown = (e, index) => {
      if (e.key === "Backspace" && !otp[index] && index > 0) {
          inputRefs.current[index - 1].focus();
      }
  };

  return (
    <Layout title="Security Auth - Diamond Admin">
    <div className="security-auth-container">
      <div className="page-header">
         <h2>Secure Auth Verification</h2>
      </div>

      <div className="auth-card">
        {/* Status Line */}
        <div className="status-line">
           Secure Auth Verification Status: 
           {isEnabled ? (
              <span className="status-badge-enabled" onClick={() => !showDisableInput && handleSendDisableCode()}>
                  Enabled
              </span>
           ) : (
              <span className="status-badge-disabled">Disabled</span>
           )}
        </div>

        {/* Enabled View - 2FA Challenge to Disable */}
        {isEnabled && (
             <div className="enabled-view">
                 {/* Only show inputs if showDisableInput is true, otherwise maybe just show status? 
                     The screenshot implies we immediately see the inputs? 
                     Actually, for security, usually you click "Disable" then get OTP.
                     But screenshot 2 shows "Security Code Verification" immediately.
                     Let's assume we triggered it or it's always visible to "verify presence" 
                     but logically we need to trigger SEND OTP first.
                     Let's keep the flow: User sees ENABLED. User clicks ENABLED or specific button.
                     Wait, user's screenshot 2 shows "Secure Auth Verification Status: Enabled" 
                     AND "Security Code Verification" form below it.
                     I implies the user is in a state where they can enter code.
                     I'll stick to: Click Status to trigger 'Send OTP' & Show Form.
                 */}
                  {!showDisableInput ? (
                    <div style={{marginTop: '20px'}}>
                         <p>
                           {authType === 'android'
                             ? 'Your account is secured with the Secure Auth Verification App.'
                             : authType === 'google'
                               ? 'Your account is secured with Google Authenticator.'
                               : 'Your account is secured with Telegram 2FA.'}
                         </p>
                         <button className="btn-primary" onClick={handleSendDisableCode} disabled={isLoading}>
                            {isLoading && !isAppAuth ? 'Sending Code...' : 'Disable Secure Auth'}
                         </button>
                    </div>
                  ) : (
                    <div className="verification-section">
                        <h3>Security Code Verification</h3>
                        <p>
                          {authType === 'android'
                            ? 'Enter the 6-digit code from your Secure Auth Verification App'
                            : authType === 'google'
                              ? 'Enter the 6-digit code from Google Authenticator'
                              : 'Enter 6-digit code from your security auth verification App'}
                        </p>
                        
                        <div className="otp-input-group">
                            {otp.map((data, index) => (
                                <input
                                    key={index}
                                    className="otp-box"
                                    type="text"
                                    maxLength="1"
                                    value={data}
                                    ref={(el) => inputRefs.current[index] = el}
                                    onChange={(e) => handleChange(e.target, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onFocus={(e) => e.target.select()}
                                    disabled={isLoading}
                                />
                            ))}
                        </div>
                        {/* Resend only applies to a pushed code — the app's code
                            rolls over by itself every 30 seconds. */}
                        {!isAppAuth && (
                          <div className="resend-container">
                              <span className="resend-link" onClick={handleSendDisableCode}>Resend Code</span>
                          </div>
                        )}
                    </div>
                  )}
             </div>
        )}

        {/* Disabled View - Setup Flow */}
        {!isEnabled && (
            <div className="disabled-view">
                 <p className="instruction-text">Please select below option to enable secure auth verification</p>
                 
                 {/* Tabs */}
                 <div className="auth-tabs">
                     <div 
                        className={`auth-tab ${activeTab === 'mobile' ? 'active' : ''}`}
                        onClick={openMobileTab}
                     >
                        Enable Using Mobile App
                     </div>
                     <div 
                        className={`auth-tab ${activeTab === 'telegram' ? 'active' : ''}`}
                        onClick={() => setActiveTab('telegram')}
                     >
                        Enable Using Telegram
                     </div>
                     <div 
                        className={`auth-tab ${activeTab === 'google' ? 'active' : ''}`}
                        onClick={() => setActiveTab('google')}
                     >
                        Enable Using Google Auth
                     </div>
                 </div>

                 <div className="tab-content">
                     {activeTab === 'mobile' && (
                         /* Reference layout: the code straight away, then the
                            download. */
                         <div className="setup-step-2">
                              <p className="step-text">Please enter below auth code in your &apos;Secure Auth Verification App&apos;.</p>
                              <div className="pair-code">
                                {pairLoading && !pairCode ? '······' : pairCode || '------'}
                              </div>
                              <p className="app-download-note">
                                 <b>If you haven&apos;t downloaded,<br />
                                 please download &apos;Secure Auth Verification App&apos; from below link.</b>
                              </p>
                              <p className="pair-expiry">Using this app you will receive auth code during login authentication</p>
                              <a className="app-download-link" href={APK_URL} target="_blank" rel="noopener noreferrer">
                                 <i className="fab fa-android"></i> Download on the Android
                              </a>
                         </div>
                     )}

                     {activeTab === 'telegram' && (
                         <>
                            {/* Step 1: Password & Get ID */}
                            {!generatedCode ? (
                                <div className="setup-step-1">
                                     <label>Please enter your login password to continue</label>
                                     <div className="input-with-button">
                                         <input 
                                             type="password" 
                                             className="form-control"
                                             placeholder=".........."
                                             value={password}
                                             onChange={(e) => setPassword(e.target.value)}
                                         />
                                         <button 
                                            className="btn-primary"
                                            onClick={handleGetConnectionStep}
                                            disabled={isLoading}
                                         >
                                             {isLoading ? 'Processing...' : 'Get Connection ID'}
                                         </button>
                                     </div>
                                </div>
                            ) : (
                                /* Step 2: Instructions */
                                <div className="setup-step-2">
                                     <p className="step-text">Please follow below instructions for the telegram 2-step verification</p>
                                     <div className="bot-instructions">
                                         <p>Find {botInfo?.botUsername
                                           ? <a href={botInfo.botLink} target="_blank" rel="noopener noreferrer">@{botInfo.botUsername}</a>
                                           : <span>(bot not configured — contact support)</span>} in your telegram and type <span className="cmd-badge">/start</span> command. Bot will respond you.</p>
                                         <p>After this type <span className="cmd-badge">/connect {generatedCode}</span> and send it to BOT.</p>
                                         <p>Now your telegram account will be linked with your website account and 2-Step verification will be enabled.</p>
                                     </div>
                                </div>
                            )}
                         </>
                     )}

                     {activeTab === 'google' && (
                         !googleSetup ? (
                             <div className="setup-step-1">
                                  <label>Please enter your login password to continue</label>
                                  <div className="input-with-button">
                                      <input
                                          type="password"
                                          className="form-control"
                                          placeholder=".........."
                                          value={googlePassword}
                                          onChange={(e) => setGooglePassword(e.target.value)}
                                      />
                                      <button
                                         className="btn-primary"
                                         onClick={handleGoogleSetup}
                                         disabled={isLoading}
                                      >
                                          {isLoading ? 'Processing...' : 'Enable Google Auth'}
                                      </button>
                                  </div>
                             </div>
                         ) : (
                             <div className="setup-step-2">
                                  <p className="step-text">Scan this QR code with the Google Authenticator app</p>
                                  <img src={googleSetup.qrCode} alt="Google Authenticator QR code" className="google-qr" />
                                  <p className="pair-expiry">
                                     Can&apos;t scan? Enter this setup key manually:<br />
                                     <span className="cmd-badge">{googleSetup.secret}</span>
                                  </p>
                                  <div className="verification-section">
                                     <h3>Security Code Verification</h3>
                                     <p>Enter the 6-digit code shown in Google Authenticator</p>
                                     <div className="otp-input-group">
                                         {googleOtp.map((data, index) => (
                                             <input
                                                 key={index}
                                                 className="otp-box"
                                                 type="text"
                                                 maxLength="1"
                                                 value={data}
                                                 ref={(el) => googleRefs.current[index] = el}
                                                 onChange={(e) => handleGoogleOtpChange(e.target, index)}
                                                 onKeyDown={(e) => handleGoogleOtpKeyDown(e, index)}
                                                 onFocus={(e) => e.target.select()}
                                                 disabled={isLoading}
                                             />
                                         ))}
                                     </div>
                                  </div>
                             </div>
                         )
                     )}
                 </div>
            </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default SecurityAuth;
